<script setup lang="ts">
import { reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastRoot,
  ToastTitle,
  ToastViewport,
} from 'reka-ui';
import IconCircleAlert from '~icons/lucide/circle-alert';
import IconCircleCheck from '~icons/lucide/circle-check';
import IconInfo from '~icons/lucide/info';
import IconX from '~icons/lucide/x';

import { toastQueue, type ToastColor, useToast } from '../composables/useToast';

/**
 * Renders the `useToast` queue — v1's Snackbar: a surface card with a purple
 * glow rising from its bottom edge and a ring that empties until the toast
 * closes (the ring is also its close button). Mount exactly one, at the app
 * root. Toasts sit under the top safe area (clear of the on-screen keyboard),
 * swipe up to dismiss, and F8 moves focus to them.
 * Figma: OMB Kit › Toast.
 */
const props = withDefaults(defineProps<{
  duration?: number;
}>(), {
  duration: 5000,
});

const { t } = useI18n({ useScope: 'global' });
const { dismiss } = useToast();

// Reka pauses every toast's timer while the viewport is hovered or focused and
// while the window is in the background; the ring pauses on the same events.
const paused = reactive(new Set<string | number>());

const icons = {
  neutral: IconInfo,
  success: IconCircleCheck,
  danger: IconCircleAlert,
} satisfies Record<ToastColor, unknown>;

const iconClass: Record<ToastColor, string> = {
  neutral: 'text-icon',
  success: 'text-success',
  danger: 'text-danger',
};
</script>

<template>
  <ToastProvider
    :label="t('ui.toast.label')"
    :duration="props.duration"
    swipe-direction="up"
  >
    <ToastRoot
      v-for="toast in toastQueue"
      :key="toast.id"
      :open="toast.open"
      :duration="toast.duration"
      :type="toast.color === 'danger' ? 'foreground' : 'background'"
      class="group pointer-events-auto relative isolate flex w-full items-center gap-3 overflow-hidden rounded-lg bg-surface p-5 text-fg data-[state=closed]:animate-toast-out data-[state=open]:animate-toast-in data-[swipe=cancel]:translate-y-0 data-[swipe=cancel]:transition-transform data-[swipe=end]:animate-toast-out data-[swipe=move]:translate-y-(--reka-toast-swipe-move-y)"
      data-testid="ui-toast"
      :data-color="toast.color ?? 'neutral'"
      :data-paused="paused.has(toast.id) ? '' : undefined"
      @pause="paused.add(toast.id)"
      @resume="paused.delete(toast.id)"
      @update:open="(open) => !open && dismiss(toast.id)"
    >
      <span
        class="pointer-events-none absolute -bottom-22 left-1/2 -z-10 size-36 -translate-x-1/2 rounded-full bg-glow-strong blur-glow"
        aria-hidden="true"
      />
      <component
        :is="icons[toast.color ?? 'neutral']"
        class="size-6 shrink-0"
        :class="iconClass[toast.color ?? 'neutral']"
        aria-hidden="true"
      />
      <div class="flex min-w-0 flex-1 flex-col gap-0.5">
        <ToastTitle
          class="text-body-lg font-semibold"
          data-testid="ui-toast-title"
        >
          {{ toast.title }}
        </ToastTitle>
        <ToastDescription
          v-if="toast.description"
          class="text-body-md text-fg-muted"
        >
          {{ toast.description }}
        </ToastDescription>
      </div>
      <ToastClose
        :aria-label="t('ui.toast.close')"
        class="relative -m-1 flex size-8 shrink-0 items-center justify-center rounded-full text-fg"
        data-testid="ui-toast-close"
      >
        <svg
          class="absolute inset-1 -rotate-90"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="11"
            stroke="currentColor"
            stroke-width="2"
            class="text-border"
          />
          <circle
            cx="12"
            cy="12"
            r="11"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            pathLength="100"
            stroke-dasharray="100"
            class="animate-countdown group-data-paused:[animation-play-state:paused] motion-reduce:animate-none"
            :style="{ animationDuration: `${toast.duration ?? props.duration}ms` }"
          />
        </svg>
        <IconX
          class="size-3.5"
          aria-hidden="true"
        />
      </ToastClose>
    </ToastRoot>

    <ToastViewport
      :label="(hotkey: string) => t('ui.toast.viewport', { hotkey })"
      class="pointer-events-none fixed inset-x-0 top-0 z-60 mx-auto flex w-full max-w-md flex-col gap-2 px-gutter pt-safe-offset-3 outline-none"
    />
  </ToastProvider>
</template>
